<script setup lang="ts">
  import { computed, watch } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import CloseIcon from '@/shared/assets/icons/CloseIcon.svg';
  import registrationPaper from '@/shared/assets/icons/RegistrationPaper.svg';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import {
    Checkbox,
    IconImage,
    IconRadio,
    ColoredButton,
    ThemeProvider,
    DateRangePicker,
  } from '@/shared/ui';
  import { useMasterList, type ValidityDateType } from '../model';
  import { useRouter } from 'vue-router';

  const props = withDefaults(
    defineProps<{
      title: string;
      onClose: () => void;
      modelValue?: boolean;
    }>(),
    {
      modelValue: false,
    }
  );

  const router = useRouter();

  const {
    listType,
    status,
    prefixType: prefixes,
    format,
    validityDateType,
    startDate,
    endDate,
    isDateRangePickerOpen,
    isDateRangeValid,
    formattedDateRange,
    resetDateRange,
    generateList,
  } = useMasterList();

  const isInternalDocument = computed(() => listType.value === 'interno');
  const isExternalDocument = computed(() => listType.value === 'externo');
  const isSpecificDateSelected = computed(() => validityDateType.value === 'especifica');
  const isAllPrefixesSelected = computed(() => prefixes.value.includes('todos'));

  const isFormValid = computed(() => {
    const baseValid = listType.value && status.value && format.value && isDateRangeValid.value;

    if (isExternalDocument.value) {
      return baseValid;
    }

    return baseValid && prefixes.value.length > 0;
  });

  const handleGenerateList = () => {
    router.push(generateList());
    props.onClose();
  };

  const emit = defineEmits<{
    'update:modelValue': [value: boolean];
    close: [];
    gerarLista: [];
  }>();

  const modelVisible = computed({
    get: () => props.modelValue,
    set: (val) => emit('update:modelValue', val),
  });

  const togglePrefix = (value: string) => {
    if (value === 'todos') {
      if (prefixes.value.includes('todos')) {
        prefixes.value = [];
      } else {
        prefixes.value = ['todos'];
      }
      return;
    }
    const withoutTodos = prefixes.value.filter((v) => v !== 'todos');
    if (withoutTodos.includes(value)) {
      prefixes.value = withoutTodos.filter((v) => v !== value);
    } else {
      prefixes.value = [...withoutTodos, value];
    }
  };

  const handleValidityDateTypeChange = (value: ValidityDateType) => {
    validityDateType.value = value;
    if (value === 'todos') {
      resetDateRange();
    }
  };

  watch(listType, (newValue) => {
    if (newValue === 'externo') {
      prefixes.value = [];
    }
  });
</script>

<template>
  <VueFinalModal
    v-model="modelVisible"
    class="flex justify-center items-center"
    content-class="flex flex-col bg-white-01 rounded-2xl w-3/4 max-w-4xl max-h-[90vh] overflow-y-auto divide-y divide-gray-02"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
  >
    <div class="flex justify-between items-center px-6 py-4">
      <h3 class="text-xl font-semibold text-gray-07">
        {{ title }}
      </h3>
      <button @click="onClose">
        <IconImage
          :src="CloseIcon"
          alt="Close"
          class="cursor-pointer"
        />
      </button>
    </div>

    <div class="flex flex-col max-h-[80vh] overflow-y-auto divide-y divide-gray-300">
      <div class="px-8 py-6 flex flex-col gap-4">
        <span class="text-sm font-semibold text-gray-07">Tipo de lista:</span>
        <div class="flex gap-8">
          <IconRadio
            label="Documentos Internos"
            value="interno"
            name="listType"
            :model-value="listType"
            @update:model-value="(val) => (listType = val)"
          />
          <IconRadio
            label="Documentos Externos"
            value="externo"
            name="listType"
            :model-value="listType"
            @update:model-value="(val) => (listType = val)"
          />
        </div>
      </div>

      <div class="px-8 py-6 flex flex-col gap-4">
        <span class="text-sm font-semibold text-gray-07">Status:</span>
        <div class="flex gap-8">
          <IconRadio
            label="Todos"
            value="todos"
            name="status"
            :model-value="status"
            @update:model-value="(val) => (status = val)"
          />
          <IconRadio
            label="Vigente"
            value="vigente"
            name="status"
            :model-value="status"
            @update:model-value="(val) => (status = val)"
          />
          <IconRadio
            v-if="isInternalDocument"
            label="Inativo"
            value="inativo"
            name="status"
            :model-value="status"
            @update:model-value="(val) => (status = val)"
          />
          <IconRadio
            v-if="isExternalDocument"
            label="Revogado"
            value="revogado"
            name="status"
            :model-value="status"
            @update:model-value="(val) => (status = val)"
          />
        </div>
      </div>

      <div
        v-if="isInternalDocument"
        class="px-8 py-6 flex flex-col gap-4"
      >
        <span class="text-sm font-semibold text-gray-07">Tipo de prefixo:</span>
        <div class="flex flex-col gap-2">
          <div class="flex gap-8">
            <div class="flex items-center gap-2">
              <Checkbox
                id="prefix-all"
                :checked="prefixes.includes('todos')"
                @toggle="togglePrefix('todos')"
              />
              <label
                for="type-prefix-all"
                class="cursor-pointer"
              >Todos</label>
            </div>
            <div class="flex items-center gap-2">
              <Checkbox
                id="prefix-MQ"
                :checked="prefixes.includes('MQ')"
                :disabled="isAllPrefixesSelected"
                @toggle="togglePrefix('MQ')"
              />
              <label
                for="type-prefix-MQ"
                class="cursor-pointer"
              >(MQ) - Manual</label>
            </div>
            <div class="flex items-center gap-2">
              <Checkbox
                id="prefix-PR"
                :checked="prefixes.includes('PR')"
                :disabled="isAllPrefixesSelected"
                @toggle="togglePrefix('PR')"
              />
              <label
                for="type-prefix-PR"
                class="cursor-pointer"
              >(PR) - Procedimento</label>
            </div>
            <div class="flex items-center gap-2">
              <Checkbox
                id="prefix-IT"
                :checked="prefixes.includes('IT')"
                :disabled="isAllPrefixesSelected"
                @toggle="togglePrefix('IT')"
              />
              <label
                for="type-prefix-IT"
                class="cursor-pointer"
              >(IT) - Instrução de Trabalho</label>
            </div>
            <div class="flex items-center gap-2">
              <Checkbox
                id="prefix-DA"
                :checked="prefixes.includes('DA')"
                :disabled="isAllPrefixesSelected"
                @toggle="togglePrefix('DA')"
              />
              <label
                for="type-prefix-DA"
                class="cursor-pointer"
              >(DA) - Dados</label>
            </div>
          </div>
        </div>
      </div>

      <div class="px-8 py-6 flex flex-col gap-4">
        <span class="text-sm font-semibold text-gray-07">Formato:</span>
        <div class="flex gap-8">
          <IconRadio
            label="Eletrônico"
            value="EL"
            name="format"
            :model-value="format"
            @update:model-value="(val) => (format = val)"
          />
          <IconRadio
            label="Eletrônico/Físico"
            value="EL_FS"
            name="format"
            :model-value="format"
            @update:model-value="(val) => (format = val)"
          />
        </div>
      </div>

      <div class="px-8 py-6 flex flex-col gap-4">
        <span class="text-sm font-semibold text-gray-07">Data de Vigência:</span>
        <div class="flex items-center gap-4">
          <IconRadio
            label="Todas"
            value="todos"
            name="validityDateType"
            :model-value="validityDateType"
            @update:model-value="handleValidityDateTypeChange"
          />
          <IconRadio
            label="Data específica"
            value="especifica"
            name="validityDateType"
            :model-value="validityDateType"
            @update:model-value="handleValidityDateTypeChange"
          />

          <button
            v-if="isSpecificDateSelected"
            type="button"
            class="flex items-center gap-2 bg-white-01 px-4 py-3 border border-gray-02 rounded-lg text-gray-07 focus:outline-none focus:border-dark-red font-medium"
            @click="isDateRangePickerOpen = true"
          >
            <IconImage
              :src="CalendarIcon"
              alt="Calendário"
              class="h-4 w-4"
            />
            {{ formattedDateRange }}
          </button>
          <DateRangePicker
            v-model:start-date="startDate"
            v-model:end-date="endDate"
            v-model:is-open="isDateRangePickerOpen"
            @apply="isDateRangePickerOpen = false"
            @cancel="isDateRangePickerOpen = false"
          />
        </div>
      </div>
    </div>

    <ThemeProvider>
      <div class="flex justify-end items-center gap-4 px-6 py-4">
        <ColoredButton
          text="Cancelar"
          :outlined="true"
          color="gray-07"
          @click="onClose"
        />
        <ColoredButton
          text="Gerar Lista"
          :icon="registrationPaper"
          :disabled="!isFormValid"
          @click="handleGenerateList"
        />
      </div>
    </ThemeProvider>
  </VueFinalModal>
</template>
